<template>
  <div class="border border-gray-200 rounded-lg overflow-hidden bg-white">
    <!-- Per-metric header — click to collapse / expand -->
    <button
      type="button"
      class="w-full px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-2 hover:bg-gray-100 transition-colors text-left"
      :class="collapsed ? 'border-b-0' : ''"
      @click="$emit('toggle-collapse')"
    >
      <div class="flex items-center gap-2 min-w-0 flex-1">
        <svg
          class="w-3 h-3 shrink-0 text-gray-500 transition-transform"
          :class="collapsed ? '' : 'rotate-90'"
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        <h4 class="text-sm font-semibold text-gray-900 truncate" :title="metric.metric_name">
          {{ metric.metric_name }}
        </h4>
      </div>
      <div class="flex items-center gap-3 shrink-0">
        <span
          v-if="metric.target != null"
          class="text-[10px] text-gray-500 tabular-nums whitespace-nowrap"
          title="Compliance target for this metric"
          @click.stop
        >Target: {{ formatTarget(metric.target, metric.display_config) }}</span>
        <span v-if="collapsed" class="text-[10px] text-gray-400 tabular-nums">
          {{ totalRowCount }} {{ totalRowCount === 1 ? 'practice' : 'practices' }}
        </span>
      </div>
    </button>

    <template v-if="!collapsed">
    <!-- Empty state -->
    <div v-if="totalRowCount === 0" class="px-3 py-6 text-xs text-gray-400 text-center">
      No practices match the current filters for this metric.
    </div>

    <table v-else class="w-full text-xs">
      <thead class="bg-gray-50 border-b border-gray-200">
        <tr class="text-left text-[10px] uppercase tracking-wider text-gray-500">
          <th class="px-3 py-1.5 font-medium min-w-[280px]">Practice</th>
          <th v-for="col in infoTagColumns" :key="'th:' + col.typeId" class="px-3 py-1.5 font-medium">{{ col.label }}</th>
          <th class="px-3 py-1.5 font-medium w-[140px]">Engagement Score</th>
          <th class="px-3 py-1.5 font-medium whitespace-nowrap">Last Engaged</th>
          <th class="px-3 py-1.5 font-medium">Performance</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="section in sections" :key="section.key">
          <tr :class="['text-[10px] uppercase tracking-wider font-semibold', section.headerClass]">
            <td :colspan="columnCount" class="px-3 py-1">{{ section.title }}</td>
          </tr>
          <tr v-if="!section.rows.length" class="border-t border-gray-100">
            <td :colspan="columnCount" class="px-3 py-3 text-xs text-gray-400">No practices in this group.</td>
          </tr>
          <tr
            v-for="row in section.rows"
            :key="`${section.key}:${row.entity_id}`"
            class="border-t border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
            @click="$emit('drill', row)"
          >
            <td
              class="px-3 py-2 text-blue-700 font-medium truncate max-w-0"
              :title="row.entity_name"
            >
              {{ row.entity_name }}
            </td>
            <td v-for="col in infoTagColumns" :key="'td:' + col.typeId + ':' + row.entity_id" class="px-3 py-2 text-gray-600 whitespace-nowrap">
              {{ (row.info_tags?.[col.typeId] || []).join(', ') || '--' }}
            </td>
            <td class="px-3 py-2" @click.stop>
              <CC_Engagement_Score_Badge :params="{ value: row.engagement_score }" :inline="true" />
            </td>
            <td
              class="px-3 py-2 text-gray-500 tabular-nums whitespace-nowrap"
              :title="row.last_engagement_date ? `Last engaged: ${formatDate(row.last_engagement_date)}` : null"
            >
              {{ daysLabel(row.days_since_last_engagement) }}
            </td>
            <td
              class="px-3 py-2"
              @mouseenter="showBulletTooltip(row, $event)"
              @mousemove="moveBulletTooltip($event)"
              @mouseleave="tooltipRow = null"
            >
              <BulletBar
                :value="barValue(row)"
                :prior-value="(metric.display_config || {}).show_efctv !== false ? row.rate_efctv : null"
                :min="barRange.min"
                :max="barRange.max"
                :label="row.denominator ? formatRate(row) : ''"
                :min-label="formatRangeEndpoint(barRange.min)"
                :max-label="formatRangeEndpoint(barRange.max)"
              />
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    </template>

    <Teleport to="body">
      <div
        v-if="tooltipRow"
        class="fixed z-50 bg-gray-900 text-white text-[11px] leading-relaxed rounded-lg shadow-lg px-3 py-2 pointer-events-none"
        :style="{ top: tooltipPos.y + 'px', left: tooltipPos.x + 'px' }"
      >
        <div class="flex items-center justify-between gap-6">
          <span class="text-gray-400">Rate</span>
          <span class="font-semibold tabular-nums">{{ formatRate(tooltipRow) }}</span>
        </div>
        <div class="flex items-center justify-between gap-6">
          <span class="text-gray-400">Numerator</span>
          <span class="tabular-nums">{{ tooltipRow.numerator?.toLocaleString() ?? '—' }}</span>
        </div>
        <div class="flex items-center justify-between gap-6">
          <span class="text-gray-400">Denominator</span>
          <span class="tabular-nums">{{ tooltipRow.denominator?.toLocaleString() ?? '—' }}</span>
        </div>
        <div v-if="tooltipRow.attribution != null" class="flex items-center justify-between gap-6">
          <span class="text-gray-400">Patients</span>
          <span class="tabular-nums">{{ tooltipRow.attribution.toLocaleString() }}</span>
        </div>
        <template v-if="tooltipRow.rate_efctv != null && (metric.display_config || {}).show_efctv !== false">
          <div class="border-t border-gray-700 mt-1.5 pt-1.5 flex items-center justify-between gap-6">
            <span class="text-gray-400">Last Year Rate</span>
            <span class="tabular-nums">{{ formatRateLike(tooltipRow.rate_efctv) }}</span>
          </div>
          <div v-if="tooltipRow.numerator_efctv != null" class="flex items-center justify-between gap-6">
            <span class="text-gray-400">Last Year Numerator</span>
            <span class="tabular-nums">{{ tooltipRow.numerator_efctv.toLocaleString() }}</span>
          </div>
          <div v-if="tooltipRow.denominator_efctv != null" class="flex items-center justify-between gap-6">
            <span class="text-gray-400">Last Year Denominator</span>
            <span class="tabular-nums">{{ tooltipRow.denominator_efctv.toLocaleString() }}</span>
          </div>
        </template>
      </div>
    </Teleport>
  </div>
</template>

<script>
import CC_Engagement_Score_Badge from '@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Engagement_Score_Badge.vue';
import BulletBar from '@/components/widgetcomponents/library/BulletBar.vue';

export default {
  name: 'PerformanceTable',
  components: { CC_Engagement_Score_Badge, BulletBar },
  props: {
    metric: { type: Object, required: true },
    // 'performance' | 'movers' — drives section titles to match the card view.
    mode: { type: String, default: 'performance' },
    // Array of { typeId, label } — one column per configured InfoTagType
    infoTagColumns: { type: Array, default: () => [] },
    collapsed: { type: Boolean, default: false },
  },
  emits: ['drill', 'toggle-collapse'],
  data() {
    return {
      tooltipRow: null,
      tooltipPos: { x: 0, y: 0 },
    };
  },
  computed: {
    sections() {
      const groups = this.metric.groups || {};
      if (this.mode === 'movers') {
        return [
          { key: 'top', title: 'Greatest Improvement', headerClass: 'bg-emerald-50 text-emerald-800', rows: groups.top || [] },
          { key: 'bottom', title: 'Least Improvement', headerClass: 'bg-red-50 text-red-800', rows: groups.bottom || [] },
        ];
      }
      const out = [
        { key: 'top', title: 'Top Performers', headerClass: 'bg-emerald-50 text-emerald-800', rows: groups.top || [] },
      ];
      // Match cards view: Movers only renders when it actually has rows.
      // Top + Bottom always render (with empty-state message inside the section).
      if ((groups.movers || []).length) {
        out.push({ key: 'movers', title: 'Movers', headerClass: 'bg-amber-50 text-amber-800', rows: groups.movers });
      }
      out.push({ key: 'bottom', title: 'Bottom Performers', headerClass: 'bg-red-50 text-red-800', rows: groups.bottom || [] });
      return out;
    },
    totalRowCount() {
      return this.sections.reduce((n, s) => n + s.rows.length, 0);
    },
    columnCount() {
      return 4 + this.infoTagColumns.length;
    },
    // Per-metric value range for the bullet bar. Mirrors legacy
    // PerformanceSummary's barRange logic, scoped to this metric's rows.
    barRange() {
      const dc = this.metric.display_config || {};
      const allRows = this.sections.flatMap(s => s.rows);
      if (dc.format === 'count') {
        const vals = allRows.map(r => r.numerator || 0).filter(v => v > 0);
        if (!vals.length) return { min: 0, max: 1 };
        return { min: 0, max: Math.max(...vals) * 1.1 };
      }
      if (dc.format === 'decimal' || dc.format === 'number' || dc.format === 'currency') {
        const vals = allRows.map(r => r.rate).filter(v => v != null);
        if (!vals.length) return { min: 0, max: 1 };
        return { min: 0, max: Math.max(...vals) * 1.1 };
      }
      // percentage / default
      return { min: 0, max: 1 };
    },
  },
  methods: {
    // Format a raw rate/value according to the metric's display_config. Used
    // directly for prior-year rate in the bullet tooltip; formatRate(row)
    // delegates here for everything except 'count' (which needs the
    // numerator). Keep both currency formatters in sync with
    // formatRangeEndpoint().
    formatRateLike(value) {
      if (value == null) return '--';
      const dc = this.metric.display_config || {};
      if (dc.format === 'decimal' || dc.format === 'number') {
        return Number(value).toFixed(dc.decimal_places ?? 1);
      }
      if (dc.format === 'currency') {
        return '$' + Math.round(Number(value)).toLocaleString();
      }
      return (value * 100).toFixed(dc.decimal_places ?? 1) + '%';
    },
    formatRate(row) {
      if (row.rate == null) return '--';
      const dc = this.metric.display_config || {};
      if (dc.format === 'count') return Number(row.numerator || 0).toLocaleString();
      return this.formatRateLike(row.rate);
    },
    showBulletTooltip(row, event) {
      this.tooltipRow = row;
      this.tooltipPos = { x: event.pageX + 12, y: event.pageY - 20 };
    },
    moveBulletTooltip(event) {
      this.tooltipPos = { x: event.pageX + 12, y: event.pageY - 20 };
    },
    formatRateTooltip(row) {
      const num = (row.numerator ?? 0).toLocaleString();
      const den = (row.denominator ?? 0).toLocaleString();
      return `Compliance: ${num} / ${den}  (${this.formatRate(row)})`;
    },
    formatTarget(target, dc) {
      const cfg = dc || {};
      if (cfg.format === 'decimal' || cfg.format === 'number') {
        return Number(target).toFixed(cfg.decimal_places ?? 1);
      }
      return (Number(target) * 100).toFixed(cfg.decimal_places ?? 0) + '%';
    },
    formatDate(d) {
      if (!d) return '—';
      const dt = new Date(d + 'T00:00:00');
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    daysLabel(d) {
      if (d == null) return '--';
      return d === 1 ? '1 day' : `${d} days`;
    },
    // Returns the value BulletBar should plot. Count metrics plot the
    // numerator; everything else plots the rate.
    barValue(row) {
      const dc = this.metric.display_config || {};
      if (!row.denominator) return null;
      return dc.format === 'count' ? (row.numerator || 0) : row.rate;
    },
    formatRangeEndpoint(v) {
      const dc = this.metric.display_config || {};
      if (dc.format === 'count') return Number(v).toLocaleString();
      if (dc.format === 'currency') return '$' + Math.round(v).toLocaleString();
      if (dc.format === 'decimal' || dc.format === 'number') {
        return Number(v).toFixed(dc.decimal_places ?? 1);
      }
      return Math.round(v * 100) + '%';
    },
  },
};
</script>
